// LB-9 §8.2 / §8.4: Paperwork rules shared by the builder components.
// Server twin: app/homes/document_access.py (types, visibility, the Reg CF
// must-be-public rule) and app/homes/listing_facts.py (filing formats).

export const DOCUMENT_TYPES = Object.freeze([
  "subscription_agreement",
  "operating_agreement",
  "ppm",
  "offering_memorandum",
  "term_sheet",
  "pro_forma",
  "financial_statements",
  "pitch_deck",
  "form_c_pdf",
  "rent_roll",
  "t12_operating_statement",
  "appraisal",
  "property_condition_report",
  "phase_i_esa",
  "loan_term_sheet",
  "market_study",
  "other",
]);

export const RE_DOCUMENT_TYPES = Object.freeze([
  "rent_roll",
  "t12_operating_statement",
  "appraisal",
  "property_condition_report",
  "phase_i_esa",
  "loan_term_sheet",
  "market_study",
]);
export const PROPERTY_HISTORY_TYPES = Object.freeze([
  "t12_operating_statement",
  "rent_roll",
]);
// Rent roll and T-12 carry tenant details: show the redaction note.
export const PII_GUIDANCE_TYPES = PROPERTY_HISTORY_TYPES;

export const VISIBILITY_PUBLIC = "public";
export const VISIBILITY_SIGNED_IN = "signed_in";
export const VISIBILITY_ACCREDITED = "accredited_verified";
// Issuer, co-editors and Invown only (never offered in the picker; set by
// the Determine Equity lender-statement uploads).
export const VISIBILITY_INTERNAL = "internal";
// What the investor-facing picker offers.
export const VISIBILITIES = Object.freeze([
  VISIBILITY_PUBLIC,
  VISIBILITY_SIGNED_IN,
  VISIBILITY_ACCREDITED,
]);

export const ASSURANCE_LEVELS = Object.freeze([
  "internal_certified",
  "cpa_reviewed",
  "cpa_audited",
]);

// OD-LB9-5 (recommendation, counsel pending): forced public on Reg CF.
export const REG_CF_REQUIRED_PUBLIC_TYPES = Object.freeze([
  "form_c_pdf",
  "financial_statements",
  "subscription_agreement",
]);
const ACCREDITED_BY_DEFAULT_ON_506C = Object.freeze([
  "rent_roll",
  "t12_operating_statement",
  "appraisal",
  "loan_term_sheet",
]);
// Regulation names per exemption: mirror of app/homes/document_access.py
// (_REG_CF_NAMES / _RULE_506C_NAMES / _RULE_506B_NAMES).
export const REG_CF_NAMES = Object.freeze(["reg_cf", "debt_reg_cf", "Reg CF"]);
export const RULE_506C_NAMES = Object.freeze([
  "506c",
  "rule_506c",
  "debt_506c",
  "Rule 506(c)",
]);
export const RULE_506B_NAMES = Object.freeze([
  "506b",
  "rule_506b",
  "debt_506b",
  "Rule 506(b)",
]);

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
// Mirror of document_access.UPLOAD_CONTENT_TYPES (no archives).
export const UPLOAD_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.webp,.tif,.tiff,.heic";
const ALLOWED_EXTENSIONS = UPLOAD_ACCEPT.split(",");

function regulationNames(home) {
  return ((home && home.regulation_used) || []).map((r) =>
    typeof r === "string" ? r : r && r.name
  );
}

export function isRegCf(home) {
  return regulationNames(home).some((n) => REG_CF_NAMES.indexOf(n) !== -1);
}

export function is506c(home) {
  return regulationNames(home).some((n) => RULE_506C_NAMES.indexOf(n) !== -1);
}

// Rule 506(b) or 506(c): the listing files a Form D.
export function isRegD(home) {
  return regulationNames(home).some(
    (n) => RULE_506C_NAMES.indexOf(n) !== -1 || RULE_506B_NAMES.indexOf(n) !== -1
  );
}

// Only http(s) URLs are ever bound to an href (a stored javascript: or
// data: URL must never become a clickable link).
export function safeDocumentUrl(url) {
  const text = String(url || "").trim();
  return /^https?:\/\//i.test(text) ? text : null;
}

// Analytics events (ids and codes only, never names or values).
export function trackPaperworkEvent(event, params = {}) {
  try {
    if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event, ...params });
    }
  } catch (e) {
    // Analytics never breaks the builder.
  }
}

// Mirror of document_access.legacy_document_type: whole words, most
// specific first ("loan term sheet" before "term sheet").
const LEGACY_NAME_TYPES = Object.freeze([
  ["loan term sheet", "loan_term_sheet"],
  ["term sheet", "term_sheet"],
  ["subscription agreement", "subscription_agreement"],
  ["pro forma", "pro_forma"],
  ["deck", "pitch_deck"],
]);

export function legacyDocumentType(name) {
  // Underscores / hyphens are spaces, camel case splits ("TermSheet"),
  // "proforma" / "termsheet" read as two words.
  const text = String(name || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\bproforma\b/g, "pro forma")
    .replace(/\btermsheet\b/g, "term sheet")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");
  for (const [needle, type] of LEGACY_NAME_TYPES) {
    const pattern = new RegExp(`\\b${needle.split(" ").join("\\s+")}\\b`);
    if (pattern.test(text)) return type;
  }
  return "other";
}

// Mirror of document_access.must_be_public: an "other" row is also judged
// by its name's legacy type.
export function mustBePublic(home, documentType, name) {
  if (!isRegCf(home)) return false;
  const types = [documentType];
  if ((!documentType || documentType === "other") && name) {
    types.push(legacyDocumentType(name));
  }
  return types.some((t) => REG_CF_REQUIRED_PUBLIC_TYPES.indexOf(t) !== -1);
}

export function defaultVisibility(home, documentType) {
  if (
    ACCREDITED_BY_DEFAULT_ON_506C.indexOf(documentType) !== -1 &&
    is506c(home) &&
    !isRegCf(home)
  ) {
    return VISIBILITY_ACCREDITED;
  }
  return VISIBILITY_PUBLIC;
}

// Client-side pre-check; the server re-checks (allowlist + 25 MB).
export function uploadProblem(file) {
  if (!file) return "missing";
  const name = String(file.name || "").toLowerCase();
  const dot = name.lastIndexOf(".");
  const ext = dot === -1 ? "" : name.slice(dot);
  if (!ext || ALLOWED_EXTENSIONS.indexOf(ext) === -1) return "file_type_not_allowed";
  if (file.size > MAX_UPLOAD_BYTES) return "file_too_large";
  if (file.size === 0) return "file_empty";
  return null;
}

// ---------------------------------------------------------------- filings
// LB-1 §7.1 formats (inline; the server is authoritative).
export const FORM_C_FILE_NUMBER_RE = /^020-\d{5,6}$/;
export const FORM_D_FILE_NUMBER_RE = /^021-\d{5,6}$/;
export const CIK_RE = /^\d{1,10}$/;
const SEC_HOSTS = ["www.sec.gov", "sec.gov"];
const SEC_PATHS = ["/Archives/edgar/data/", "/cgi-bin/browse-edgar"];

export function secUrlProblem(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  let url;
  try {
    url = new URL(text);
  } catch (e) {
    return "format";
  }
  if (url.protocol !== "https:" || /\s/.test(text)) return "format";
  if (SEC_HOSTS.indexOf(url.host.toLowerCase()) === -1) return "format";
  if (!SEC_PATHS.some((p) => url.pathname.indexOf(p) === 0)) return "format";
  return null;
}

export function cikInSecUrl(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  let url;
  try {
    url = new URL(text);
  } catch (e) {
    return null;
  }
  let match = /^\/Archives\/edgar\/data\/0*(\d{1,10})(?:\/|$)/.exec(url.pathname);
  if (!match) match = /(?:^|&)CIK=0*(\d{1,10})(?:&|$)/i.exec(url.search.replace(/^\?/, ""));
  if (!match) return null;
  return match[1].replace(/^0+/, "") || "0";
}

// field -> problem code ("format" | "cik_mismatch") for the filing inputs.
export function filingProblems(values) {
  const v = values || {};
  const problems = {};
  const has = (k) => String(v[k] || "").trim() !== "";
  if (has("form_c_url") && secUrlProblem(v.form_c_url)) problems.form_c_url = "format";
  if (has("form_d_url") && secUrlProblem(v.form_d_url)) problems.form_d_url = "format";
  if (has("sec_file_number") && !FORM_C_FILE_NUMBER_RE.test(String(v.sec_file_number).trim()))
    problems.sec_file_number = "format";
  if (has("form_d_file_number") && !FORM_D_FILE_NUMBER_RE.test(String(v.form_d_file_number).trim()))
    problems.form_d_file_number = "format";
  if (has("issuer_cik") && !CIK_RE.test(String(v.issuer_cik).trim()))
    problems.issuer_cik = "format";
  if (!problems.form_c_url && !problems.issuer_cik && has("form_c_url") && has("issuer_cik")) {
    const linked = cikInSecUrl(v.form_c_url);
    const cik = String(v.issuer_cik).trim().replace(/^0+/, "") || "0";
    if (linked !== null && linked !== cik) problems.form_c_url = "cik_mismatch";
  }
  return problems;
}

export const PAPERWORK_KEYS = Object.freeze([
  "form_c_url",
  "sec_file_number",
  "issuer_cik",
  "form_d_url",
  "form_d_file_number",
  "is_first_time_reg_cf_issuer",
  "prior_12mo_reg_cf_raised_cents",
  "risk_factors",
  "custom_risks",
]);

// The update_home payload for the Paperwork keys, from a loaded home.
export function paperworkPayloadFromHome(home) {
  const h = home || {};
  return {
    form_c_url: h.form_c_url || null,
    sec_file_number: h.sec_file_number || null,
    issuer_cik: h.issuer_cik || null,
    form_d_url: h.form_d_url || null,
    form_d_file_number: h.form_d_file_number || null,
    is_first_time_reg_cf_issuer:
      h.is_first_time_reg_cf_issuer === undefined ? null : h.is_first_time_reg_cf_issuer,
    prior_12mo_reg_cf_raised_cents:
      h.prior_12mo_reg_cf_raised_cents === undefined
        ? null
        : h.prior_12mo_reg_cf_raised_cents,
    risk_factors: Array.isArray(h.risk_factors) ? h.risk_factors.slice() : [],
    custom_risks: (Array.isArray(h.custom_risks) ? h.custom_risks : []).map((r) => ({
      id: r.id,
      title: r.title,
      body: r.body || "",
    })),
  };
}
